import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { refAPI, annoncesAPI } from '../services/api';
import AnnonceCard from '../components/AnnonceCard';
import './HomePage.css';

export default function HomePage() {
  const navigate = useNavigate();
  const [territoires, setTerritoires] = useState([]);
  const [categories, setCategories] = useState([]);
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [searchTerr, setSearchTerr] = useState('');

  useEffect(() => {
    Promise.all([
      refAPI.territoires(),
      refAPI.categories(),
      annoncesAPI.liste({ limite: 8, tri: 'recent' }),
    ]).then(([t, c, a]) => {
      setTerritoires(t.data);
      setCategories(c.data);
      setAnnonces(a.data.annonces);
    }).finally(() => setLoading(false));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQ) params.set('q', searchQ);
    if (searchTerr) params.set('territoire', searchTerr);
    navigate(`/annonces?${params.toString()}`);
  };

  return (
    <div className="home">

      {/* ── Hero ───────────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-blob hero-blob-1" />
          <div className="hero-blob hero-blob-2" />
        </div>
        <div className="container hero-content">
          <div className="hero-text fade-up fade-up-1">
            <span className="hero-eyebrow">La plateforme des DOM-TOM 🏝️</span>
            <h1 className="hero-title">
              Achetez & vendez<br />
              <span className="hero-title-accent">près de chez vous</span>
            </h1>
            <p className="hero-sub">
              Annonces gratuites pour Guadeloupe, Martinique, Réunion, Guyane et tous les territoires d'outre-mer.
            </p>
          </div>

          <form className="hero-search fade-up fade-up-2" onSubmit={handleSearch}>
            <input
              className="hero-search-input"
              placeholder="Que recherchez-vous ?"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
            />
            <select
              className="hero-search-select"
              value={searchTerr}
              onChange={e => setSearchTerr(e.target.value)}
            >
              <option value="">Tous les territoires</option>
              {territoires.map(t => (
                <option key={t.id} value={t.slug}>{t.nom}</option>
              ))}
            </select>
            <button type="submit" className="hero-search-btn">Rechercher</button>
          </form>

          <div className="hero-stats fade-up fade-up-3">
            <div className="hero-stat"><strong>11</strong> Territoires</div>
            <div className="hero-stat-sep" />
            <div className="hero-stat"><strong>100%</strong> Gratuit</div>
            <div className="hero-stat-sep" />
            <div className="hero-stat"><strong>DOM-TOM</strong> Seulement</div>
          </div>
        </div>
      </section>

      {/* ── Territoires ────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Choisissez votre département ou territoire</h2>
            <p className="section-sub">Annonces locales, contacts locaux</p>
          </div>
          <div className="territoires-grid">
            {territoires.map((t, i) => (
              <Link
                key={t.id}
                to={`/annonces?territoire=${t.slug}`}
                className="territoire-card fade-up"
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <span className="territoire-flag">{flagEmoji(t.code)}</span>
                <span className="territoire-nom">{t.nom}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Catégories ─────────────────────────────────────────── */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Toutes les catégories</h2>
          </div>
          <div className="categories-grid">
            {categories.map((c, i) => (
              <Link
                key={c.id}
                to={`/annonces?categorie=${c.slug}`}
                className="categorie-card fade-up"
                style={{ animationDelay: `${i * 0.04}s`, '--cat-color': c.couleur }}
              >
                <span className="categorie-icone">{catIcone(c.icone)}</span>
                <span className="categorie-nom">{c.nom}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Annonces récentes ───────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Dernières annonces</h2>
            <Link to="/annonces" className="section-link">Voir tout →</Link>
          </div>
          {loading ? (
            <div className="loader"><div className="loader-spin" /></div>
          ) : (
            <div className="annonces-grid">
              {annonces.map(a => <AnnonceCard key={a.id} annonce={a} />)}
            </div>
          )}
          <div className="section-cta">
            <Link to="/annonces" className="btn btn-primary">Voir toutes les annonces</Link>
            <Link to="/annonces/nouveau" className="btn btn-secondary">Déposer une annonce</Link>
          </div>
        </div>
      </section>

      {/* ── Comment ça marche ───────────────────────────────────── */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Comment ça marche ?</h2>
          </div>
          <div className="steps-grid">
            {[
              { n: '01', icon: '📝', titre: 'Déposez', desc: 'Créez votre annonce en 2 minutes, gratuitement' },
              { n: '02', icon: '💬', titre: 'Échangez', desc: 'Discutez directement avec les acheteurs via la messagerie' },
              { n: '03', icon: '🤝', titre: 'Concluez', desc: 'Rencontrez-vous pour finaliser la transaction en main propre' },
            ].map((s, i) => (
              <div key={i} className="step-card">
                <div className="step-number">{s.n}</div>
                <div className="step-icon">{s.icon}</div>
                <h3 className="step-titre">{s.titre}</h3>
                <p className="step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}

// Helpers
function flagEmoji(code) {
  const flags = {
    GP: '🇬🇵', MQ: '🇲🇶', GF: '🇬🇫', RE: '🇷🇪',
    YT: '🇾🇹', NC: '🇳🇨', PF: '🇵🇫', MF: '🇲🇫',
    BL: '🇧🇱', PM: '🇵🇲', WF: '🇼🇫',
  };
  return flags[code] || '🏝️';
}

function catIcone(nom) {
  const map = {
    car: '🚗', home: '🏠', smartphone: '📱', sofa: '🛋️',
    shirt: '👕', music: '🎵', briefcase: '💼', tool: '🔧',
    heart: '🐾', hammer: '🔨', coffee: '🍽️', grid: '📦',
  };
  return map[nom] || '📦';
}
